import { t } from '@lingui/core/macro';
import { updatePublicSession, userSession } from 'state/session';
import { showToast } from 'state/toast';
import { myCameraStream } from 'state/video';
import { user } from 'utils/auth/user';
import logger from 'utils/log';
import { sendApiRequest } from 'utils/network';
import { RTCClient } from 'webrtc';
import { EStreamSessionEvents } from 'webrtc/events';
import { SignalerClient } from 'webrtc/signal';
import { isDebugMode } from './debug';

export let broadcastingConnection: SignalerClient;
export let voipRtcConnection: RTCClient;
export let broadcastingConnectionId: string;

async function getIceServers() {
  // if ICE_SERVERS_ORIGIN is not set, it will go to the default value which is fine for our deployed instances
  return sendApiRequest<RTCIceServer[]>('/ice-servers', { origin: process.env.ICE_SERVERS_ORIGIN });
}

export function initSessionBroadcasting() {
  const broadcastingApiUrl = `${process.env.SIGNALING_ORIGIN}?sessionId=${userSession().get('id')}&topicId=session`;

  if (broadcastingConnection && broadcastingConnection.isReady()) {
    if (broadcastingConnection.wsApiUrl !== broadcastingApiUrl) {
      // URL changed, disconnect and reconnect
      console.warn('[Session Broadcast] Broadcasting connection URL changed, disconnecting');
      broadcastingConnection.disconnect();
    } else {
      // same URL, no need to reconnect
      return;
    }
  }

  broadcastingConnection = new SignalerClient(broadcastingApiUrl);
  broadcastingConnection.connect().then((connection) => {
    broadcastingConnectionId = connection.connectionId;
  });
  logger.log('[Session Broadcast] Broadcasting connection initialized');

  userSession().markRequirementReady('broadcastingEstablished');

  broadcastingConnection.on(EStreamSessionEvents.SessionUpdated, (data) => {
    logger.log('[Session Broadcast] Session update received', data);
    userSession().setValues(data);
    updatePublicSession.cancel();
  });
}

export async function initWebRtc(
  sessionId = userSession().get('id'),
  callback?: () => void
): Promise<RTCClient | null> {
  // only remote sessions can initialize webrtc and voip should be allowed
  if (!userSession().voipEnabled) {
    // voip not allowed
    return null;
  }

  // already connected, call callback immediately if provided
  if (voipRtcConnection?.sessionId === sessionId) {
    if (callback) {
      callback();
    }
    return voipRtcConnection;
  }

  // if presentation if completed but we try to init rtc, then something is wrong.
  // proceeding is not allowed and also make sure that the previous connection is properly destroyed
  if (userSession().presentationCompleted) {
    voipRtcConnection?.destroy();
    return null;
  }

  // Make sure that camera stream is started before initializing the RTC connection
  // This way we ensure we have all the information used for RTC handshake transmission
  if (!myCameraStream().stream) {
    return new Promise<RTCClient | null>((resolve, reject) => {
      let unsubscribeStarted: (() => void) | null = null;
      let unsubscribeError: (() => void) | null = null;

      const onStreamStarted = () => {
        cleanup();
        initWebRtc(sessionId, callback).then(resolve).catch(reject);
      };

      const onStreamError = () => {
        cleanup();
        resolve(null);
      };

      const cleanup = () => {
        if (unsubscribeStarted) unsubscribeStarted();
        if (unsubscribeError) unsubscribeError();
      };

      unsubscribeStarted = myCameraStream().once('stream:started', onStreamStarted);
      unsubscribeError = myCameraStream().once('stream:error', onStreamError);

      myCameraStream().init();
    });
  }

  const iceServers = await getIceServers();
  if (!iceServers) {
    console.error(`No ICE servers found, RTC won't work`);
    return null;
  }
  voipRtcConnection = RTCClient.getInstance(
    iceServers,
    `${process.env.SIGNALING_ORIGIN}?sessionId=${sessionId}&topicId=user-stream&userType=${user.type}`
  );
  voipRtcConnection.sessionId = sessionId;

  if (callback) {
    voipRtcConnection.on('voip:connected', callback);
  }

  await voipRtcConnection.connect();

  voipRtcConnection.on('peer:disconnected', () => {
    showToast({
      title: t`Your peer disconnected from the VOIP session`,
      type: 'warn',
      notificationId: 'VOIP_PEER_DISCONNECTED',
    });
  });

  voipRtcConnection.on('peer:connected', () => {
    showToast({
      title: t`Your peer connected to the VOIP session`,
      type: 'success',
      notificationId: 'VOIP_PEER_CONNECTED',
    });
  });

  userSession().once('session:end', () => {
    voipRtcConnection?.destroy();
  });

  if (isDebugMode()) {
    // @ts-expect-error - expose config in global scope for debugging
    window.voipRtcConnection = voipRtcConnection;
  }

  return voipRtcConnection;
}
